import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { API_BASE_URL } from '../config/constants';
import { getLanguage } from '../utils/monacoUtils';

function FileModal({ isOpen, onClose, filePath, inline = false }) {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const onCloseRef = useRef(onClose);
  const editorRef = useRef(null);

  // Track if there are unsaved changes
  const hasUnsavedChanges = content !== originalContent;

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Fetch file content when modal opens
  useEffect(() => {
    if (isOpen && filePath) {
      fetchFileContent();
    } else {
      setContent('');
      setOriginalContent('');
      setError(null);
      setSaveStatus(null);
    }
  }, [isOpen, filePath]);

  // Handle keyboard shortcuts (Escape to close, Cmd+S to save)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
      // Cmd+S (Mac) or Ctrl+S (Windows/Linux) to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        if (content !== originalContent && !saving) {
          saveFile();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasUnsavedChanges, content, originalContent, saving]);

  const fetchFileContent = async () => {
    setLoading(true);
    setError(null);
    setSaveStatus(null);

    try {
      const pathParts = filePath.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : filePath;
      const encodedPath = encodeURIComponent(relativePath);
      const response = await fetch(`${API_BASE_URL}/read?path=${encodedPath}`, {
        method: 'GET',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to read file: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      let fileContent = '';

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        fileContent = data.content || data || '';
      } else {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          fileContent = data.content || data || '';
        } catch (e) {
          fileContent = text;
        }
      }

      setContent(fileContent);
      setOriginalContent(fileContent);
    } catch (err) {
      console.error('Error fetching file content:', err);
      setError(err.message);
      setContent('');
      setOriginalContent('');
    } finally {
      setLoading(false);
    }
  };

  const saveFile = async () => {
    setSaving(true);
    setSaveStatus(null);

    try {
      const pathParts = filePath.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : filePath;

      const response = await fetch('${API_BASE_URL}/write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ path: relativePath, content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save file: ${response.status} ${response.statusText}`);
      }

      setOriginalContent(content);
      setSaveStatus({ type: 'success', message: 'Saved!' });

      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Error saving file:', err);
      setSaveStatus({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;

    // Add Cmd+S / Ctrl+S save shortcut
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (content !== originalContent && !saving) {
        saveFile();
      }
    });

    // Focus the editor
    editor.focus();
  };

  const handleEditorChange = (value) => {
    setContent(value || '');
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const fileName = filePath.split('/').pop();
  const language = getLanguage(filePath);

  const editorContent = (
    <div
      style={{
        background: '#1e1e1e',
        borderRadius: inline ? '0' : '12px',
        boxShadow: inline ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.4)',
        width: inline ? '100%' : '90%',
        maxWidth: inline ? 'none' : '1200px',
        height: inline ? '100%' : '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Inline mode: floating status bar - COMMENTED OUT
      {inline && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'rgba(37, 37, 38, 0.9)',
          border: '1px solid #3c3c3c',
          padding: '6px 12px',
          borderRadius: '6px',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}>
          <span style={{
            fontSize: '13px',
            color: '#e1e1e1',
            fontFamily: 'Monaco, Menlo, "Courier New", monospace',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            📄 {fileName}
            {hasUnsavedChanges && (
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#f59e0b'
              }} title="Unsaved changes" />
            )}
          </span>
          {saving ? (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Saving...</span>
          ) : saveStatus ? (
            <span style={{ fontSize: '11px', color: saveStatus.type === 'success' ? '#22c55e' : '#ef4444' }}>
              {saveStatus.message}
            </span>
          ) : null}
          <button
            onClick={saveFile}
            disabled={!hasUnsavedChanges || saving}
            style={{
              background: hasUnsavedChanges ? '#22c55e' : '#4a4a4a',
              color: hasUnsavedChanges ? '#fff' : '#888',
              border: 'none',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: hasUnsavedChanges && !saving ? 'pointer' : 'default',
              opacity: hasUnsavedChanges ? 1 : 0.6,
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              if (hasUnsavedChanges && !saving) {
                e.currentTarget.style.background = '#16a34a';
              }
            }}
            onMouseLeave={(e) => {
              if (hasUnsavedChanges) {
                e.currentTarget.style.background = '#22c55e';
              }
            }}
          >
            Save
          </button>
        </div>
      )}
      */}

      {/* Header - hidden in inline mode */}
      {!inline && (
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #3c3c3c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#252526'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#e1e1e1',
            fontFamily: 'Monaco, Menlo, "Courier New", monospace'
          }}>
            <span>📄</span>
            <span>{fileName}</span>
            {hasUnsavedChanges && (
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#f59e0b'
              }} title="Unsaved changes" />
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {saving ? (
              <span style={{ fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                Saving...
              </span>
            ) : saveStatus ? (
              <span style={{
                fontSize: '12px',
                color: saveStatus.type === 'success' ? '#22c55e' : '#ef4444',
                fontFamily: 'monospace'
              }}>
                {saveStatus.message}
              </span>
            ) : hasUnsavedChanges ? (
              <span style={{ fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                <kbd style={{
                  background: '#3c3c3c',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  border: '1px solid #4a4a4a'
                }}>⌘S</kbd> to save
              </span>
            ) : null}

            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                color: '#9ca3af',
                fontSize: '18px',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3c3c3c';
                e.currentTarget.style.color = '#e1e1e1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            Loading file...
          </div>
        ) : error ? (
          <div style={{
            padding: '20px',
            background: '#3c1f1f',
            color: '#ef4444',
            borderRadius: '6px',
            fontSize: '13px',
            margin: '20px'
          }}>
            Error: {error}
          </div>
        ) : (
          <Editor
            height="100%"
            language={language}
            value={content}
            theme="vs-dark"
            onMount={handleEditorMount}
            onChange={handleEditorChange}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'off',
              automaticLayout: true,
              tabSize: 2,
              folding: true,
              lineNumbersMinChars: 4,
              padding: { top: inline ? 48 : 16 }
            }}
          />
        )}
      </div>

      {/* Footer - hidden in inline mode */}
      {!inline && (
        <div style={{
          padding: '10px 20px',
          borderTop: '1px solid #3c3c3c',
          background: '#252526',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#858585',
          fontFamily: 'Monaco, Menlo, "Courier New", monospace'
        }}>
          <span>{filePath}</span>
          <span>{content.split('\n').length} lines • {language}</span>
        </div>
      )}
    </div>
  );

  // In inline mode, render directly without portal
  if (inline) {
    return editorContent;
  }

  return createPortal(
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}
    >
      {editorContent}
    </div>,
    document.body
  );
}

export default FileModal;
